/**
 * @namespace Money
 */
import { toNumber, isNaN } from "lodash/fp";

/**
 * @name Currency
 * @memberof Money
 * @description This is the list of all of the currencies that our system supports.
 */
export type Currency = "USD";

/**
 * @name Money
 * @memberof Money
 * @description Represents money! The type is generic in the currency so that we can rely on the type system to prevent us from doing things--like math--across different currencies.
 * This is the type that we use when we're working with the backend Money type, as it's a BigDecimal, which serializes/deserializes to a string.
 */
export type Money<C extends Currency> = {
  amount: string;
  currency: C;
};

/**
 * @name money
 * @memberof Money
 * @description Constructs a value of the Money type. See below for currency-specific constructors.
 * This is the type that we use when we're working with the backend Money type, as it's a BigDecimal, which serializes/deserializes to a string.
 */
export const money = <C extends Currency>(
  currency: C,
  m: string | number | Record<string, string | number>,
): Money<C> => {
  if (m === null || m === undefined)
    throw new Error("Cannot create money from nothing.");

  switch (typeof m) {
    case "string": {
      const n = toNumber(m);
      if (isNaN(n)) throw new Error(`Cannot convert ${m} to number.`);
      return money(currency, n);
    }

    case "object": {
      if (!m.currency) throw new Error("Currency is missing from object.");

      if (typeof m.currency !== "string")
        throw new Error("Cannot create money without a currency.");

      if (m.currency !== currency)
        throw new Error(`Cannot create ${currency} from ${m.currency}.`);

      return money(currency, m.amount);
    }

    case "number":
      return {
        amount: m.toString(),
        currency,
      };

    default:
      throw new Error(`Cannot create money from value of type ${typeof m}.`);
  }
};

/**
 * @name usd
 * @memberof Money
 * @description Constructors a value of USD money, specifically.
 */
export const usd = (
  m: string | number | Record<string, string | number>,
): Money<"USD"> => money("USD", m);

/**
 * Regular expression to validate user input for money values.
 *
 * Pattern: /^\d*(\.\d{0,2})?$/
 *
 * - Allows an optional decimal point with up to 2 digits after it.
 * - Accepts whole numbers (e.g., "123") and decimal values (e.g., "123.45").
 * - Leading zeroes are allowed (e.g., "001.99").
 * - Enforces a digit before the decimal (e.g., ".99" is invalid, "0.99" is valid).
 * - Does **not** allow negative numbers or commas.
 * - Designed for **user input**, not final currency formatting.
 *
 * Examples of valid inputs:
 * - "0"
 * - "123"
 * - "123.4"
 * - "123.45"
 *
 * Examples of invalid inputs:
 * - "123.456"
 * - "12.3.4"
 * - ".99"
 * - "abc"
 * - "-10"
 */
export const userInputMoneyRegex = /^\d+(\.\d{0,2})?$/;

export const userInputMoneyIsValid = (value: string): boolean =>
  userInputMoneyRegex.test(value);

/**
 * Corrects a user input string to a standardized money format with 2 decimal places.
 * Returns `null` if the input is not a valid number.
 *
 * @param input - The user input string (e.g. "3", "4.5", ".99")
 * @returns A string formatted to 2 decimal places (e.g. "3.00", "4.50", "0.99"), or null if invalid
 */
export function formatToTwoDecimals(input: string | number): string | null {
  if (typeof input === "string" && input.trim() === "") return null;
  const num = Number(input);
  return isNaN(num) ? null : num.toFixed(2);
}
